import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { Button } from "./Button";
import { apiClient } from "../../../zodios/client";
import { sleep } from "../../../hooks/api";
import { errorToast } from "../../../utils/toasts";

interface GetReportButtonProps {
  queryDetails: {
    id: number;
    query_type: string;
  };
}

type StartReportPayload = {
  query_type: string;
  subscription_id: number;
};

const GetReportButton = ({ queryDetails }: GetReportButtonProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["amc"]);

  // Codes come from StartReportErrorCode in amc_app/views/reports.py; anything unmapped falls back to the
  // generic line, so a new server code degrades instead of showing an empty toast.
  const startReportErrorMessage = (code: string | undefined) => {
    switch (code) {
      case "SUBSCRIPTION_NOT_FOUND":
        return t(($) => $.amc["This report is no longer available. Refresh the page and try again."]);
      case "SUBSCRIPTION_HAS_NO_TEMPLATE":
        return t(($) => $.amc["This report is not linked to a template. Contact support."]);
      case "WORKFLOW_TEMPLATE_NOT_FOUND":
        return t(($) => $.amc["The template for this report is no longer available. Contact support."]);
      case "REPORT_NOT_STARTED":
        return t(($) => $.amc["Your account is missing the data this report needs. Contact support."]);
      default:
        return t(($) => $.amc["Please try again or contact support."]);
    }
  };

  const startReportMutation = useMutation({
    mutationFn: async (data: StartReportPayload) => {
      await apiClient.startReport({
        query_type: data.query_type,
        subscription_id: data.subscription_id,
      });
    },
    onError: (error) => {
      const code = isAxiosError<{ code?: string } | undefined>(error) ? error.response?.data?.code : undefined;
      errorToast({
        title: t(($) => $.amc["Failed to generate report"]),
        message: startReportErrorMessage(code),
        error,
      });
    },
  });

  const reportStatusQuery = useQuery({
    queryKey: ["getReportStatus", queryDetails.id],
    queryFn: async () => {
      await sleep(15000);
      while (true) {
        const res = await apiClient.getReportStatus({
          queries: {
            subscription_id: queryDetails.id,
          },
        });

        switch (res.status) {
          case "SUCCEEDED":
            startReportMutation.reset();
            void queryClient.invalidateQueries({ queryKey: ["amc_reports"] });
            return res;
          case "FAILED":
            throw new Error(t(($) => $.amc["Report generation failed."]));
          case "PENDING":
          case "RUNNING":
          default:
            await sleep(30000);
        }
      }
    },
    enabled: startReportMutation.isSuccess,
  });

  const handleGenerateReport = () => {
    startReportMutation.mutate({
      query_type: queryDetails.query_type,
      subscription_id: queryDetails.id,
    });
  };

  const isReportPending =
    startReportMutation.isPending || (reportStatusQuery.isFetching && reportStatusQuery.data?.status !== "SUCCEEDED");

  return (
    <Button onPress={handleGenerateReport} isPending={isReportPending}>
      {isReportPending ? t(($) => $.amc["Generating..."]) : t(($) => $.amc["Generate Report"])}
    </Button>
  );
};

export default GetReportButton;
